import { useCallback, useEffect, useRef, useState } from "react";
import {
  clientToNormalized,
  normalizedToPixels,
} from "../../utils/videoCoords.js";
import styles from "./LessonRoom.module.css";

export const ANNOTATION_MSG = "annotation";

export const ANNOTATION_COLORS = [
  "#ff3b30", // red
  "#ff9500", // orange
  "#a2845e", // brown
  "#ffcc00", // yellow
  "#34c759", // green
  "#00c7be", // teal
  "#007aff", // blue
  "#5856d6", // indigo
  "#af52de", // purple
  "#ffffff", // white
  "#1c1c1e", // black
];

export const DEFAULT_ANNOTATION_COLOR = ANNOTATION_COLORS[0];

const STROKE_MIN_DIST = 0.008;
const STROKE_MAX_POINTS = 120;

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function paintPolyline(ctx, videoEl, points, color, lineWidth) {
  if (!points || points.length < 2) return;
  ctx.beginPath();
  points.forEach(([nx, ny], i) => {
    const p = normalizedToPixels(videoEl, nx, ny);
    if (!p) return;
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.stroke();
}

function paintLine(ctx, videoEl, shape, color, lineWidth) {
  paintPolyline(
    ctx,
    videoEl,
    [
      [shape.x1, shape.y1],
      [shape.x2, shape.y2],
    ],
    color,
    lineWidth
  );
}

function paintShape(ctx, videoEl, shape, fallbackColor, lineWidth = 3) {
  const color = shape.color || fallbackColor || DEFAULT_ANNOTATION_COLOR;
  if (shape.tool === "line") paintLine(ctx, videoEl, shape, color, lineWidth);
  else if (shape.tool === "stroke") {
    paintPolyline(ctx, videoEl, shape.points, color, lineWidth);
  }
}

function drawShapes(ctx, videoEl, shapes, draft) {
  const { clientWidth, clientHeight } = videoEl;
  ctx.clearRect(0, 0, clientWidth, clientHeight);

  shapes.forEach((s) => paintShape(ctx, videoEl, s));

  if (draft) {
    paintShape(ctx, videoEl, draft, draft.color, 2);
  }
}

function thinPoints(points) {
  if (points.length <= STROKE_MAX_POINTS) return points;
  const step = Math.ceil(points.length / STROKE_MAX_POINTS);
  const thinned = [];
  for (let i = 0; i < points.length; i += step) {
    thinned.push(points[i]);
  }
  const last = points[points.length - 1];
  const end = thinned[thinned.length - 1];
  if (!end || end[0] !== last[0] || end[1] !== last[1]) {
    thinned.push(last);
  }
  return thinned;
}

/**
 * Transparent canvas over a screen-share <video>.
 * Coordinates are normalized 0–1 relative to letterboxed video content.
 */
export default function AnnotationOverlay({
  videoRef,
  enabled,
  tool,
  color = DEFAULT_ANNOTATION_COLOR,
  shapes,
  setShapes,
  sendAppMessage,
  canAnnotate = true,
}) {
  const canvasRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const draftRef = useRef(null);
  const dragStartRef = useRef(null);
  const strokePointsRef = useRef(null);
  const colorRef = useRef(color);

  useEffect(() => {
    colorRef.current = color;
  }, [color]);

  const updateDraft = useCallback((next) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef?.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawShapes(ctx, video, shapes, draft);
  }, [videoRef, shapes, draft]);

  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef?.current;
    if (!canvas || !video) return;
    const w = video.clientWidth;
    const h = video.clientHeight;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    redraw();
  }, [videoRef, redraw]);

  useEffect(() => {
    syncCanvasSize();
    const video = videoRef?.current;
    if (!video) return undefined;

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => syncCanvasSize())
        : null;
    if (ro) ro.observe(video);

    const onLoaded = () => syncCanvasSize();
    video.addEventListener("loadedmetadata", onLoaded);
    window.addEventListener("resize", syncCanvasSize);

    return () => {
      if (ro) ro.disconnect();
      video.removeEventListener("loadedmetadata", onLoaded);
      window.removeEventListener("resize", syncCanvasSize);
    };
  }, [videoRef, syncCanvasSize]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const broadcast = useCallback(
    (payload) => {
      if (typeof sendAppMessage === "function") {
        sendAppMessage({ type: ANNOTATION_MSG, ...payload }, "*");
      }
    },
    [sendAppMessage]
  );

  const onPointerDown = (e) => {
    if (!enabled || !canAnnotate || tool === "none") return;
    const video = videoRef?.current;
    if (!video) return;

    const norm = clientToNormalized(video, e.clientX, e.clientY);
    if (!norm) return;

    e.preventDefault();
    canvasRef.current?.setPointerCapture?.(e.pointerId);
    const activeColor = colorRef.current;

    if (tool === "line") {
      dragStartRef.current = norm;
      updateDraft({
        tool: "line",
        color: activeColor,
        x1: norm.x,
        y1: norm.y,
        x2: norm.x,
        y2: norm.y,
      });
      return;
    }

    if (tool === "stroke") {
      const points = [[norm.x, norm.y]];
      strokePointsRef.current = points;
      updateDraft({ tool: "stroke", color: activeColor, points: [...points] });
    }
  };

  const onPointerMove = (e) => {
    if (!enabled || !canAnnotate || tool === "none") return;
    const video = videoRef?.current;
    if (!video) return;

    const norm = clientToNormalized(video, e.clientX, e.clientY);
    if (!norm) return;

    if (tool === "line" && dragStartRef.current) {
      const start = dragStartRef.current;
      updateDraft({
        tool: "line",
        color: colorRef.current,
        x1: start.x,
        y1: start.y,
        x2: norm.x,
        y2: norm.y,
      });
      return;
    }

    if (tool === "stroke" && strokePointsRef.current) {
      const points = strokePointsRef.current;
      const last = points[points.length - 1];
      const dx = norm.x - last[0];
      const dy = norm.y - last[1];
      if (Math.sqrt(dx * dx + dy * dy) >= STROKE_MIN_DIST) {
        points.push([norm.x, norm.y]);
        updateDraft({
          tool: "stroke",
          color: colorRef.current,
          points: [...points],
        });
      }
    }
  };

  const onPointerUp = (e) => {
    if (!enabled || !canAnnotate) return;
    const current = draftRef.current;

    if (tool === "line" && dragStartRef.current && current?.tool === "line") {
      const dx = current.x2 - current.x1;
      const dy = current.y2 - current.y1;
      if (Math.sqrt(dx * dx + dy * dy) > 0.01) {
        const shape = {
          id: makeId(),
          tool: "line",
          color: current.color || colorRef.current,
          x1: current.x1,
          y1: current.y1,
          x2: current.x2,
          y2: current.y2,
        };
        setShapes((prev) => [...prev, shape]);
        broadcast({
          tool: "line",
          id: shape.id,
          color: shape.color,
          x1: shape.x1,
          y1: shape.y1,
          x2: shape.x2,
          y2: shape.y2,
        });
      }
      updateDraft(null);
      dragStartRef.current = null;
    }

    if (tool === "stroke" && strokePointsRef.current) {
      const raw = strokePointsRef.current;
      strokePointsRef.current = null;
      if (raw.length >= 2) {
        const points = thinPoints(raw);
        const shape = {
          id: makeId(),
          tool: "stroke",
          color: current?.color || colorRef.current,
          points,
        };
        setShapes((prev) => [...prev, shape]);
        broadcast({
          tool: "stroke",
          id: shape.id,
          color: shape.color,
          points: shape.points,
        });
      }
      updateDraft(null);
    }

    try {
      canvasRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      className={styles.annotationCanvas}
      style={{
        pointerEvents: !canAnnotate || tool === "none" ? "none" : "auto",
        cursor: !canAnnotate || tool === "none" ? "default" : "crosshair",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    />
  );
}
